import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { LoginRequest, LoginResponse, User } from '../shared/models/api.models';
import { ApiConfigService } from '../shared/services/api-config.service';
import { logger } from '../shared/services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private isBrowser: boolean;

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiConfig: ApiConfigService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    // Vérifier si un token existe au démarrage (uniquement côté client)
    if (this.isBrowser) {
      this.loadStoredAuth();
    }
  }

  /**
   * Connexion de l'utilisateur
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.apiConfig.authEndpoints.login, credentials)
      .pipe(
        tap(response => {
          // Stocker le token et les infos utilisateur (uniquement côté client)
          if (this.isBrowser) {
            localStorage.setItem('access_token', response.access_token);
            localStorage.setItem('user', JSON.stringify(response.user));
          }
          this.currentUserSubject.next(response.user);
        })
      );
  }

  /**
   * Déconnexion de l'utilisateur
   */
  logout(): void {
    if (this.isBrowser) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Vérifier si l'utilisateur est connecté
   */
  isAuthenticated(): boolean {
    if (!this.isBrowser) {
      return false; // Pas d'authentification côté serveur
    }
    const token = localStorage.getItem('access_token');
    return !!token && !this.isTokenExpired(token);
  }

  /**
   * Obtenir le token d'accès
   */
  getToken(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    return localStorage.getItem('access_token');
  }

  /**
   * Obtenir l'utilisateur actuel
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Charger l'authentification stockée
   */
  private loadStoredAuth(): void {
    // Double vérification pour la sécurité SSR
    if (!this.isBrowser || typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return; // Ne pas charger côté serveur
    }
    
    const token = localStorage.getItem('access_token');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr && !this.isTokenExpired(token)) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
        logger.log('Authentification restaurée pour:', user.email);
      } catch (error) {
        logger.error('Erreur lors du parsing des données utilisateur:', error);
        this.logout();
      }
    } else {
      // Token expiré ou inexistant, nettoyer le localStorage
      if (token || userStr) {
        logger.log('Token expiré ou invalide, nettoyage du localStorage');
        this.logout();
      }
    }
  }

  /**
   * Vérifier si le token est expiré
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch (error) {
      return true;
    }
  }

  /**
   * Demander la réinitialisation du mot de passe
   */
  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiConfig.baseUrl}${this.apiConfig.authEndpoints.forgotPassword}`, { email });
  }

  /**
   * Réinitialiser le mot de passe avec le token
   */
  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiConfig.baseUrl}${this.apiConfig.authEndpoints.resetPassword}`, { 
      token, 
      newPassword 
    });
  }
}
